// src/pages/AdminPanel.jsx

import React, { useState, useEffect, useMemo } from "react";
import {
  Card,
  Button,
  Popconfirm,
  message,
  Typography,
  Divider,
  Table,
  Input,
  Select,
  Space,
  Tag,
  Badge,
} from "antd";
import {
  ExclamationCircleOutlined,
  UserOutlined,
  TeamOutlined,
} from "@ant-design/icons";
import { Container, Row, Col } from "react-bootstrap";
import axios from "axios";
import { useSelector } from "react-redux";
import config from "../config";

const { Title, Text } = Typography;
const { Search } = Input;
const baseUrl = config.baseUrl;

const AdminPanel = () => {
  const token = useSelector((state) => state.auth.user?.token);
  const [sessions, setSessions] = useState([]);
  const [filterLogin, setFilterLogin] = useState("");
  const [selectedGroups, setSelectedGroups] = useState([]);

  // Cargar lista de sesiones
  const fetchSessions = async () => {
    try {
      const { data } = await axios.get(`${baseUrl}/api/adminKepler/sessions`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true,
      });
      setSessions(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
      message.error("No se pudieron cargar las sesiones.");
    }
  };

  useEffect(() => {
    fetchSessions();
  }, []);

  // Extraer grupos únicos de las sesiones
  const availableGroups = useMemo(() => {
    const groupsMap = new Map();
    sessions.forEach((session) => {
      if (session.group_id && session.grupo) {
        groupsMap.set(session.group_id, {
          id: session.group_id,
          name: session.grupo,
        });
      }
    });
    return Array.from(groupsMap.values()).sort((a, b) =>
      a.name.localeCompare(b.name)
    );
  }, [sessions]);

  // Contar sesiones por grupo
  const getSessionCountByGroup = (groupId) => {
    return sessions.filter((s) => s.group_id === groupId).length;
  };

  // Forzar logout global
  const handleForceLogoutGlobal = async () => {
    try {
      await axios.post(
        `${baseUrl}/api/adminKepler/force-logout`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true,
        }
      );
      message.success("Todas las sesiones han sido cerradas.");
      fetchSessions();
    } catch (err) {
      console.error(err);
      message.error("Error al forzar desconexión global.");
    }
  };

  // Forzar logout de un usuario según su OID
  const handleForceLogoutUser = async (userOid) => {
    try {
      await axios.post(
        `${baseUrl}/api/adminKepler/force-logout/${userOid}`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true,
        }
      );
      message.success(`Sesión del usuario ${userOid} cerrada.`);
      fetchSessions();
    } catch (err) {
      console.error(err);
      message.error(`Error al cerrar sesión de usuario ${userOid}.`);
    }
  };

  // Forzar logout por grupos
  const handleForceLogoutByGroups = async () => {
    if (selectedGroups.length === 0) {
      message.warning("Debes seleccionar al menos un grupo.");
      return;
    }

    try {
      const { data } = await axios.post(
        `${baseUrl}/api/adminKepler/force-logout-groups`,
        { groupIds: selectedGroups },
        {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true,
        }
      );
      message.success(data.message || "Sesiones cerradas correctamente.");
      setSelectedGroups([]);
      fetchSessions();
    } catch (err) {
      console.error(err);
      message.error("Error al cerrar sesiones por grupos.");
    }
  };

  // Columnas de la tabla
  const columns = [
    {
      title: "Login",
      dataIndex: "loginUser",
      key: "loginUser",
      align: "center",
      width: 150,
    },
    {
      title: "Grupo",
      dataIndex: "grupo",
      key: "grupo",
      render: (grupo, record) => <Tag color="blue">{grupo}</Tag>,
      width: 150,
    },
    {
      title: "IP",
      dataIndex: "last_ip",
      key: "last_ip",
      align: "center",
      width: 130,
    },
    {
      title: "Último login",
      dataIndex: "last_login",
      key: "last_login",
      width: 180,
    },
    {
      title: "Acción",
      key: "action",
      fixed: "right",
      width: 130,
      render: (_, record) => (
        <Button
          danger
          size="small"
          onClick={() => handleForceLogoutUser(record.user)}
        >
          Cerrar sesión
        </Button>
      ),
    },
  ];

  // Filtrar sesiones por login y por grupos seleccionados
  const filteredSessions = useMemo(() => {
    return sessions.filter((s) => {
      const matchesLogin = s.loginUser
        ?.toLowerCase()
        .includes(filterLogin.trim().toLowerCase());
      const matchesGroup =
        selectedGroups.length === 0 || selectedGroups.includes(s.group_id);
      return matchesLogin && matchesGroup;
    });
  }, [sessions, filterLogin, selectedGroups]);

  // Obtener cantidad de usuarios afectados por los grupos seleccionados
  const affectedUsersCount = useMemo(() => {
    if (selectedGroups.length === 0) return 0;
    return sessions.filter((s) => selectedGroups.includes(s.group_id)).length;
  }, [sessions, selectedGroups]);

  return (
    <Container className="py-5">
      <Row>
        {/* Card en la izquierda */}
        <Col xs={12} md={3} className="mb-4">
          <Card
            variant="default"
            style={{
              borderRadius: 8,
              boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
            }}
            className="p-4 h-100"
          >
            <div className="text-center">
              <ExclamationCircleOutlined
                style={{ fontSize: 48, color: "#faad14" }}
              />
              <Title level={4} className="mt-2">
                Panel de Administración
              </Title>
            </div>
            <Divider />
            <Text>
              Controla las sesiones activas: cierra todas, individuales o por
              grupos.
            </Text>

            {/* Botón de cerrar todas */}
            <div className="d-grid gap-2 mt-4">
              <Popconfirm
                title="¿Seguro que deseas cerrar todas las sesiones?"
                icon={<ExclamationCircleOutlined style={{ color: "red" }} />}
                onConfirm={handleForceLogoutGlobal}
                okText="Sí, cerrar"
                cancelText="Cancelar"
              >
                <Button type="primary" danger size="large" block>
                  🔒 Cerrar todas las sesiones
                </Button>
              </Popconfirm>
            </div>

            <Divider />

            {/* Sección de cierre por grupos */}
            <div className="mt-3">
              <Space
                direction="vertical"
                style={{ width: "100%" }}
                size="middle"
              >
                <div className="d-flex align-items-center gap-2">
                  <TeamOutlined style={{ fontSize: 20, color: "#1890ff" }} />
                  <Text strong>Cerrar por grupos</Text>
                </div>

                <Select
                  mode="multiple"
                  style={{ width: "100%" }}
                  placeholder="Selecciona grupos"
                  value={selectedGroups}
                  onChange={setSelectedGroups}
                  allowClear
                  maxTagCount="responsive"
                  showSearch
                  filterOption={(input, option) =>
                    option.children.toLowerCase().includes(input.toLowerCase())
                  }
                >
                  {availableGroups.map((group) => (
                    <Select.Option key={group.id} value={group.id}>
                     
                      <span style={{ marginRight: 25 }}>{group.name}</span>
                    </Select.Option>
                  ))}
                </Select>

                {selectedGroups.length > 0 && (
                  <div>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {selectedGroups.length} grupo(s) seleccionado(s)
                    </Text>
                    <br />
                    <Text strong style={{ fontSize: 12, color: "#ff4d4f" }}>
                      {affectedUsersCount} sesión(es) se cerrarán
                    </Text>
                  </div>
                )}

                <Popconfirm
                  title="¿Cerrar sesiones de usuarios en estos grupos?"
                  description={
                    <div>
                      <p>Se cerrarán las sesiones de:</p>
                      <p>
                        <strong>{affectedUsersCount} usuario(s)</strong>
                      </p>
                      <p>pertenecientes a {selectedGroups.length} grupo(s).</p>
                    </div>
                  }
                  icon={
                    <ExclamationCircleOutlined style={{ color: "orange" }} />
                  }
                  onConfirm={handleForceLogoutByGroups}
                  okText="Sí, cerrar"
                  cancelText="Cancelar"
                  disabled={selectedGroups.length === 0}
                >
                  <Button
                    type="default"
                    danger
                    block
                    disabled={selectedGroups.length === 0}
                    icon={<TeamOutlined />}
                  >
                    Cerrar por grupos
                  </Button>
                </Popconfirm>
              </Space>
            </div>
          </Card>
        </Col>

        {/* Tabla a la derecha */}
        <Col xs={12} md={9}>
          <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
            <Search
              placeholder="Buscar por login"
              allowClear
              onChange={(e) => setFilterLogin(e.target.value)}
              style={{ width: 300 }}
            />

            <Button type="primary" onClick={fetchSessions}>
              🔄 Refrescar
            </Button>
          </div>

          <Card>
            <div className="mb-3 d-flex gap-2 flex-wrap">
              <Tag color="blue" icon={<UserOutlined />}>
                {sessions.length} sesión(es) total(es)
              </Tag>
              <Tag color="green" icon={<TeamOutlined />}>
                {availableGroups.length} grupo(s) activo(s)
              </Tag>
              {(filterLogin || selectedGroups.length > 0) && (
                <Tag color="orange">
                  Mostrando: {filteredSessions.length} sesión(es)
                </Tag>
              )}
            </div>

            <div style={{ overflowX: "auto" }}>
              <Table
                rowKey="oid"
                dataSource={filteredSessions}
                columns={columns}
                pagination={{
                  pageSize: 10,
                  showTotal: (total) => `Total: ${total} sesiones`,
                  showSizeChanger: true,
                  pageSizeOptions: ["5", "10", "20", "50"],
                }}
                scroll={{ x: "max-content" }}
                size="small"
              />
            </div>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default AdminPanel;
